
export default class BasicModel {
   constructor(data = {}) {
     this.pointRadius = 15;
     this.active = data.active !== false;
     this.selected = false;
     this.highlighted = false;
     this.highlightedModifier = data.highlightedModifier;
     this.variation = data.variation;
     this.origin = data.origin;
     this.points = data.points;
     this.modifiers = data.modifiers;
     this.size = data.size;
     this.rotation = data.rotation;
     this.marker = data.marker;
     this.context = data.context;
  };

  x = () => this.origin[0] * this.context.scale;

  y = () => this.origin[1] * this.context.scale;

  canRotate = () => this.rotation !== undefined;

  setVariationColors = () => {
    if (this.variation === "red") {
      this.context.fillStyle = "rgb(240,85,85)";
      this.context.strokeStyle = "rgba(205, 20, 20, 1)";
    } else if (this.variation === "green") {
      this.context.fillStyle = "rgba(150, 210, 120, 1)";
      this.context.strokeStyle = "rgba(90, 130, 75, 1)";
    } else if (this.variation === "blue") {
      this.context.fillStyle = "rgba(85, 150, 240, 1)";
      this.context.strokeStyle = "rgba(50, 80, 180, 1)";
    } else if (this.variation === "yellow") {
      this.context.fillStyle = "rgba(250, 250, 128, 1)";
      this.context.strokeStyle = "rgba(168, 168, 0, 1)";
    }
  };

  setupContext = () => {
    this.context.lineCap = "round";
    this.context.setLineDash([]);
    this.context.lineWidth = 1;

    this.rotateContext(this.rotation);
    this.renderSelection();
    this.renderBoundingBox();
    this.renderRotationHandle();

    this.context.globalAlpha = this.active ? 1 : 0.2;
    this.context.fillStyle = "rgba(128, 128, 128, 1)";
    this.context.strokeStyle = "rgba(0, 0, 0, 1)";

    this.setVariationColors();
  };

  resetContext = () => {
    this.context.globalAlpha = 1;
    this.rotateContext(-this.rotation);
  };

  rotateContext = (angle) => {
    if (this.canRotate()) {
      this.context.translate(this.x(), this.y());
      this.context.rotate((angle * Math.PI) / 180);
      this.context.translate(-this.x(), -this.y());
    }
  };

  renderBoundingBox = () => {
    const frame = this.boundingBox();
    if (this.highlighted && frame) {
      this.context.beginPath();
      this.context.lineWidth = 1;
      this.context.strokeStyle = "rgba(255, 255, 255, 0.5)";
      this.context.rect(frame.x - 5, frame.y - 5, frame.w + 10, frame.h + 10);
      this.context.stroke();
    }
  };

  renderRotationHandle = () => {
    if (this.selected && this.canRotate()) {
      this.context.beginPath();
      this.context.fillStyle = "rgba(255, 0, 0, 0.6)";
      this.context.strokeStyle = "rgba(255, 0, 0,0.6)";
      this.context.arc(this.x(), this.y() + 50, 5, -0.5 * Math.PI, 1.5 * Math.PI, true);
      this.context.lineTo(this.x(), this.y());
      this.context.stroke();
      this.context.fill();
    }
  };

  renderSelection = () => {
    if (this.selected) {
      this.context.beginPath();
      this.context.fillStyle = "rgba(255, 255, 255, 0.2)";
      this.context.arc(this.x(), this.y(), this.pointRadius, 0, 2 * Math.PI, true);
      this.context.fill();
    }
  };

  hitTest = (x, y) => {
    let dx, dy;

    // Origin
    dx = this.origin[0] - x;
    dy = this.origin[1] - y;

    if (dx ** 2 + dy ** 2 < this.pointRadius ** 2) {
      return {
        type: "origin",
        position: this.origin.slice(0),
      };
    }

    // Modifiers
    for (let p in this.modifiers) {
      const modifier = this.modifiers[p];

      dx = modifier.origin[0] - x;
      dy = modifier.origin[1] - y;
      const r = modifier.radius * this.context.scale;

      if (dx ** 2 + dy ** 2 < r ** 2) {
        return {
          type: "modifier",
          index: p,
          position: modifier.origin.slice(),
        };
      }
    }

    // Control points
    for (let p in this.points) {
      const point = this.points[p];

      dx = point[2] - x;
      dy = point[3] - y;

      if (dx ** 2 + dy ** 2 < this.pointRadius ** 2) {
        return {
          type: "control",
          index: p,
          position: point.slice(2),
        };
      }

      dx = point[0] - x;
      dy = point[1] - y;

      if (dx ** 2 + dy ** 2 < this.pointRadius ** 2) {
        return {
          type: "quad",
          index: p,
          position: point.slice(0, 2),
        };
      }
    }

    if (this.canRotate()) {
      const handleX = -50 * Math.sin((this.rotation * Math.PI) / 180);
      const handleY = 50 * Math.cos((this.rotation * Math.PI) / 180);

      dx = this.origin[0] + handleX - x;
      dy = this.origin[1] + handleY - y;

      if (dx ** 2 + dy ** 2 < this.pointRadius ** 2) {
        return { type: "rotation" };
      }
    }

    return undefined;
  };

  boundingBox = () => {
    let minX = this.x();
    let maxX = this.x();
    let minY = this.y();
    let maxY = this.y();

    for (let p in this.points) {
      const point = this.points[p];

      if (point[2] < minX) minX = point[2];
      if (point[2] > maxX) maxX = point[2];
      if (point[3] < minY) minY = point[3];
      if (point[3] > maxY) maxY = point[3];
    }

    return {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY,
    };
  };
}

